import React, { memo, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import { HeaderBackButton } from '@react-navigation/elements';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Animated, { interpolate, useAnimatedStyle } from 'react-native-reanimated';
import Text from 'design-system/Text';
import Logo from 'design-system/Logo';
import Spacer from 'design-system/Spacer';
import { rem } from 'design-system/values';
import BlurAmountView from 'design-system/BlurAmountView';
import BackButton from 'design-system/BackButton';
import useColors from 'hooks/useColors';
import { useAppSelector } from 'store/hooks';
import { formatAmount } from 'utils/formatting';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { isAndroid, isWeb } from '../../../constants';
import StockAlertButton from './StockAlertButton';
import ShareStockInfoButton from './ShareStockInfoButton';
import AddToWatchListButton from './AddToWatchListButton';
import { useStockMeta, useCurrentPrice } from '../hooks/selectors';
import { selectIsTradingAccountAlpaca } from '../reducers/selectors';
const StockScreenHeader = ({ assetId, scrollY, hasStockAlerts, webContentInset }) => {
    const isAlpacaAccount = useAppSelector(selectIsTradingAccountAlpaca);
    const stockCache = useStockMeta(assetId);
    const colors = useColors();
    const { top } = useSafeAreaInsets();
    const navigation = useMainStackNavigation();
    const currentPrice = useCurrentPrice(assetId);
    const scrambled = useAppSelector((store) => store.utils.scrambled);
    const uri = stockCache?.iconUrl;
    const price = useMemo(() => {
        if (currentPrice) {
            return formatAmount(currentPrice?.value, currentPrice?.currency, 2);
        }
        return ' ';
    }, [currentPrice]);
    // The logo starts where the cards' content does: column inset, card margin 16, card padding 16.
    // The back button and the gap after it already take WEB_TITLE_START.
    const webTitleStyle = webContentInset === undefined
        ? undefined
        : {
            marginLeft: Math.max(0, webContentInset + WEB_CARD_CONTENT_INSET - WEB_TITLE_START),
            marginRight: webContentInset + WEB_CARD_CONTENT_INSET - rem(16),
        };
    const animatedStyle = useAnimatedStyle(() => ({
        opacity: interpolate(scrollY.value, [40, 60], [0, 1], 'clamp'),
    }), [scrollY]);
    return (<View style={[styles.mainContainer, isWeb && styles.mainContainerWeb, { marginTop: top }]}>
      {/* On web, the same back button and header height as the other web screens */}
      {isWeb ? (<BackButton />) : (<View style={styles.backButtonContainer}>
          <HeaderBackButton displayMode="minimal" onPress={navigation.pop} tintColor={colors.elements.primary}/>
        </View>)}
      <Animated.View style={webTitleStyle ? [styles.container, webTitleStyle, animatedStyle] : [styles.container, animatedStyle]}>
        <Logo R32 uri={uri} resizeMode="contain"/>
        <Spacer w={12}/>
        <View style={styles.infoContainer}>
          {isAlpacaAccount ? (<>
              <Text Text-16 Gray numberOfLines={1} style={styles.symbolText}>
                {stockCache?.symbol ? `($${stockCache.symbol})` : ''}
              </Text>
              <BlurAmountView enabled={scrambled}>
                <Text Numbers-16 style={styles.amountShort}>
                  {price}
                </Text>
              </BlurAmountView>
            </>) : (!!stockCache?.name && (<Text Text-16 numberOfLines={1} style={styles.symbolText}>
                {stockCache.name}
              </Text>))}
        </View>
      </Animated.View>
      {isAlpacaAccount ? (<View style={styles.addToWatchListButton}>
          {!!hasStockAlerts && (<>
              <StockAlertButton assetId={assetId}/>
              <Spacer w={16}/>
            </>)}
          <AddToWatchListButton assetId={assetId}/>
          <ShareStockInfoButton assetId={assetId}/>
        </View>) : null}
    </View>);
};
// Back button (16 margin, 8 padding each side, 24 icon) plus the 8 gap after it
const WEB_TITLE_START = 64;
const WEB_CARD_CONTENT_INSET = rem(32);
const styles = StyleSheet.create({
    mainContainer: {
        flex: 1,
        height: rem(46),
        flexDirection: 'row',
        paddingRight: rem(16),
        paddingLeft: isAndroid ? 8 : 0,
    },
    mainContainerWeb: {
        flexGrow: 0,
        flexShrink: 0,
        flexBasis: 'auto',
        height: 64,
        alignItems: 'center',
        gap: rem(8),
    },
    backButtonContainer: {
        width: 44,
        height: rem(46),
        justifyContent: 'center',
        alignItems: 'center',
    },
    container: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
    },
    amountShort: {
        marginLeft: rem(8),
    },
    addToWatchListButton: {
        paddingLeft: rem(16),
        flexDirection: 'row',
        alignItems: 'center',
    },
    infoContainer: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
    },
    symbolText: {
        flexShrink: 1,
    },
});
export default memo(StockScreenHeader);
